import { useIntl } from 'react-intl';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

type LocaleCheckType = 'session' | 'intl';

interface UseCheckIsLocaleEnglishOptions {
	localeChecks?: LocaleCheckType[];
}

const isLocaleStringEnglish = (locale = '') => {
	const englishPrefix = 'en';
	return locale.toLowerCase().startsWith(englishPrefix);
};

/**
 * This hook checks the locale against specified sources such as session data
 * and internationalization data.
 *
 * @param options - The options for checking the locale.
 * @param options.localeChecks - The sources to check for the locale. Defaults to ['session', 'intl'].
 * @returns - True if all specified locales are English. Returns false otherwise.
 *
 * @example
 * const isEnglish = useCheckIsLocaleEnglish({
 *   localeChecks: ['intl', 'session'],
 * });
 */
export const useCheckIsLocaleEnglish = ({
	localeChecks = ['session', 'intl'],
}: UseCheckIsLocaleEnglishOptions = {}): boolean => {
	const { locale: intlLocale = '' } = useIntl() || {};
	const { locale: sessionLocale = '' } = useSessionData() || {};

	const localeCheckers = {
		session: () => isLocaleStringEnglish(sessionLocale),
		intl: () => isLocaleStringEnglish(intlLocale),
	};

	return localeChecks.every((check) => localeCheckers[check]());
};
